/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Printer } from 'lucide-react';
import { LegalDocument } from '../types';

interface LegalPageProps {
  doc: LegalDocument;
  onBack: () => void;
}

export default function LegalPage({ doc, onBack }: LegalPageProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      id="legal-page"
      className="w-full min-h-screen bg-[#080A0A] text-white pt-28 pb-20 px-5 md:px-8 select-text"
    >
      <article className="max-w-4xl mx-auto">
        <header className="mb-11 pb-8 border-b border-white/[0.07]">
          <div className="flex items-start justify-between gap-5">
            <div>
              <p className="text-[11px] tracking-[0.2em] font-bold text-[#00F5D4] mb-4">KOEMORI LEGAL</p>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-4">{doc.title}</h1>
              {doc.subtitle && doc.subtitle !== doc.title && (
                <p className="text-sm text-white/42 mb-2">{doc.subtitle}</p>
              )}
              <p className="text-sm text-white/35">{doc.effectiveDate}</p>
            </div>
            <button
              onClick={() => window.print()}
              className="print:hidden shrink-0 w-10 h-10 rounded-xl border border-white/[0.08] bg-white/[0.035] text-white/45 hover:text-[#00F5D4] hover:border-[#00F5D4]/25 flex items-center justify-center transition-all cursor-pointer"
              aria-label="このページを印刷"
              title="このページを印刷"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className="space-y-9 text-sm md:text-[15px] leading-8 text-white/70 print:text-black">
          {doc.sections.map((section, sectionIndex) => (
            <section key={sectionIndex} className="legal-section space-y-4">
              {section.title && (
                <h2 className="text-lg md:text-xl font-extrabold text-white pt-2 print:text-black">
                  {section.title}
                </h2>
              )}

              {section.content && (
                Array.isArray(section.content) ? (
                  <div className="space-y-3">
                    {section.content.map((paragraph, paragraphIndex) => (
                      <p key={paragraphIndex} className="whitespace-pre-wrap">{paragraph}</p>
                    ))}
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{section.content}</p>
                )
              )}

              {section.subsections && (
                <div className="space-y-6 pl-4 md:pl-6 border-l border-[#00F5D4]/18 print:border-black/15">
                  {section.subsections.map((subsection, subsectionIndex) => (
                    <div key={subsectionIndex} className="space-y-2.5">
                      <h3 className="font-bold text-white/92 print:text-black">{subsection.title}</h3>
                      {Array.isArray(subsection.content) ? (
                        subsection.content.map((paragraph, paragraphIndex) => (
                          <p key={paragraphIndex} className="whitespace-pre-wrap">{paragraph}</p>
                        ))
                      ) : (
                        <p className="whitespace-pre-wrap">{subsection.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {section.table && (
                <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.025] print:border-black/15 print:bg-white">
                  <div className="divide-y divide-white/[0.07] print:divide-black/10">
                    {section.table.map((row, rowIndex) => (
                      <div key={rowIndex} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 p-4 md:p-6">
                        <div className="md:col-span-4 text-xs font-bold text-[#00F5D4] print:text-black">
                          {row.label}
                        </div>
                        <div className="md:col-span-8 text-sm text-white/68 whitespace-pre-wrap print:text-black">
                          {row.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          ))}
        </div>

        <footer className="mt-16 pt-8 border-t border-white/[0.07] print:hidden">
          <button
            onClick={onBack}
            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/[0.045] border border-white/[0.07] hover:border-[#00F5D4]/25 hover:text-[#00F5D4] text-white/65 font-semibold text-sm transition-all cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            戻る
          </button>
        </footer>
      </article>
    </motion.div>
  );
}
